/** Message thread + composer — Messenger-style bubbles. Own messages right
 *  (blue), others left (grey) with avatar; group threads label the sender.
 *  Photos render inline, videos play inline. Polls while open. */
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, ImagePlus, Loader2, Minus, PencilLine, Phone, Reply, Send, Trash2, Users, Video, X } from "lucide-react";
import { toast } from "sonner";
import Avatar, { minutesAway } from "./Avatar";
import { deleteMessage, fetchMessages, getConversation, markRead, mediaUrl, removeMember, renameConversation, sendMessage, sendTyping, uploadChatMedia } from "./chatApi";
import { requestCall } from "./callApi";
import { resetPageZoom } from "./useVisualViewport";

const sameMinuteGroup = (a, b) =>
  a && b && !a.call && !b.call && a.sender_id === b.sender_id &&
  Math.abs(new Date(b.created_at) - new Date(a.created_at)) < 5 * 60 * 1000;

const fmtCallDuration = (s) => {
  if (s == null) return null;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const h = Math.floor(m / 60);
  return h ? `${h}:${String(m % 60).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

/** "2:18 p.m." today; "Oct 20, 2:18 p.m." on other days (year only if different). */
const fmtMsgTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const time = d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
    if (d.toDateString() === now.toDateString()) return time;
    const date = d.toLocaleDateString("en-CA", {
      month: "short", day: "numeric",
      ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    });
    return `${date}, ${time}`;
  } catch { return ""; }
};

/** Messenger-style call entry in the thread history: completed calls show
 *  time + duration; unanswered ones show as missed/declined attempts and
 *  are tappable to call back. */
function CallLogRow({ msg, isGroup, onCallBack }) {
  const c = msg.call;
  const Icon = c.kind === "video" ? Video : Phone;
  const base = c.kind === "video" ? "video call" : "voice call";
  const label = c.status === "completed" ? base : c.status === "declined" ? `Declined ${base}` : `Missed ${base}`;
  const dur = fmtCallDuration(c.duration_seconds);
  let when = "";
  try {
    when = new Date(c.started_at || msg.created_at).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  } catch { /* bad date */ }
  const missed = c.status !== "completed";
  const inner = (
    <>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate font-medium">
        {isGroup && msg.sender_name ? `${msg.sender_name.split(" ")[0]} · ` : ""}{label}
      </span>
      {dur && <span className="shrink-0">· {dur}</span>}
      {when && <span className={`shrink-0 ${missed ? "text-red-300" : "text-gray-400"}`}>· {when}</span>}
    </>
  );
  const pillCls = `flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-[11px] ${missed ? "bg-red-50 text-red-500" : "bg-gray-200/70 text-gray-500"}`;
  return (
    <div className="mt-2 flex justify-center" data-testid={`chat-call-${msg.message_id}`}>
      {missed && onCallBack ? (
        <button type="button" onClick={onCallBack} title="Call back" data-testid={`chat-callback-${msg.message_id}`}
          className={`${pillCls} transition-colors hover:bg-red-100 hover:text-red-600`}>
          {inner}
        </button>
      ) : (
        <div className={pillCls}>{inner}</div>
      )}
    </div>
  );
}

function Attachment({ fileId, kind, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    mediaUrl(fileId).then((u) => alive && setUrl(u));
    return () => { alive = false; };
  }, [fileId]);
  if (!url) {
    return <div className="flex h-32 w-48 items-center justify-center rounded-2xl bg-black/10"><Loader2 className="h-5 w-5 animate-spin text-white/70" /></div>;
  }
  if (kind === "video") {
    return <video src={url} controls playsInline className="max-h-64 max-w-[240px] rounded-2xl bg-black" data-testid="chat-video" />;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={name} className="max-h-64 max-w-[240px] rounded-2xl object-cover" data-testid="chat-image" />
    </a>
  );
}

export default function ChatThread({ me, convo, people, isAdmin, onBack, onMinimize, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState(null); // message_id awaiting delete confirm
  const [showMembers, setShowMembers] = useState(false);
  const [liveConvo, setLiveConvo] = useState(convo); // fresh read-receipt state
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [mention, setMention] = useState(null); // {query, start, end} while typing @
  const [mentionIdx, setMentionIdx] = useState(0);
  const [replyTo, setReplyTo] = useState(null); // message being quoted
  const [, forceTick] = useState(0);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const composerRef = useRef(null);
  const stickToBottom = useRef(true);
  const lastTypingSent = useRef(0);
  const lpTimer = useRef(null);   // long-press-to-reply
  const lpFired = useRef(false);

  const startLongPress = (m) => {
    if (m.deleted || m.call) return;
    lpFired.current = false;
    lpTimer.current = setTimeout(() => {
      lpFired.current = true;
      try { navigator.vibrate?.(30); } catch { /* unsupported */ }
      setReplyTo(m);
      composerRef.current?.focus();
    }, 500);
  };
  const cancelLongPress = () => {
    if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; }
  };

  const person = (uid) => people.find((p) => p.user_id === uid) || convo.members?.find((m) => m.user_id === uid) || {};

  // Mention picker: members matching the fragment after the caret's "@".
  const mentionOptions = mention
    ? (convo.members || [])
        .filter((mm) => mm.user_id !== me && mm.name && mm.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, 6)
    : [];

  const insertMention = (member) => {
    const before = draft.slice(0, mention.start);
    const after = draft.slice(mention.end);
    const inserted = `@${member.name} `;
    setDraft(before + inserted + after);
    setMention(null);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (el) {
        el.focus();
        const p = (before + inserted).length;
        el.setSelectionRange(p, p);
      }
    });
  };

  const onComposerKeyDown = (e) => {
    if (mention && mentionOptions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionOptions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionOptions.length) % mentionOptions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionOptions[mentionIdx] || mentionOptions[0]); return; }
      if (e.key === "Escape") { setMention(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); }
  };

  // "typing…" heartbeat — at most one ping per 3s while the draft is
  // non-empty; the server-side flag expires 6s after the last ping.
  const onDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    const now = Date.now();
    if (v && now - lastTypingSent.current > 3000) {
      lastTypingSent.current = now;
      sendTyping(convo.conversation_id).catch(() => {});
    }
    // mention picker: an "@" fragment right before the caret (groups only)
    if (!convo.is_group) { setMention(null); return; }
    const pos = e.target.selectionStart ?? v.length;
    const upto = v.slice(0, pos);
    const m = upto.match(/(^|\s)@([\w ]{0,25})$/);
    if (m) {
      setMention({ query: m[2], start: pos - m[2].length - 1, end: pos });
      setMentionIdx(0);
    } else {
      setMention(null);
    }
  };

  // Highlight @mentions of any member inside message bodies.
  const LINK_RE = /(https?:\/\/[^\s]+|\/dashboard\?[^\s]+)/g;
  const renderWithMentions = (text, mine) => {
    const names = (convo.members || []).map((m) => m.name).filter(Boolean);
    const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
    const re = names.length ? new RegExp(`(@(?:${esc.join("|")}))`, "gi") : null;
    const mentions = (chunk, key) => {
      if (!re || !chunk.includes("@")) return chunk;
      return chunk.split(re).map((part, i) =>
        part.startsWith("@") && names.some((n) => part.toLowerCase() === `@${n.toLowerCase()}`) ? (
          <span key={`${key}-${i}`} className={`font-bold ${mine ? "text-amber-200" : "text-[#1a3fd1]"}`}>{part}</span>
        ) : (
          part
        ),
      );
    };
    // URLs and in-app links (/dashboard?tab=…) become clickable — EliteBot's alerts rely on this
    return text.split(LINK_RE).map((part, i) =>
      /^(https?:\/\/|\/dashboard\?)/.test(part) ? (
        <a key={i} href={part} target={part.startsWith("/") ? "_self" : "_blank"} rel="noreferrer" data-testid="chat-link"
          className={`underline underline-offset-2 break-all ${mine ? "text-amber-100" : "text-[#1a3fd1] dark:text-[#8fa8ff]"}`}>{part.startsWith("/") ? "Open in app" : part}</a>
      ) : (
        mentions(part, i)
      ),
    );
  };

  const load = async (silent = true) => {
    try {
      // Messages + the conversation summary (members' last_read_at) together
      // so the "Seen" receipt under your last message updates within a poll.
      const [docs, fresh] = await Promise.all([
        fetchMessages(convo.conversation_id),
        getConversation(convo.conversation_id).catch(() => null),
      ]);
      // Always replace: same IDs can still mean changed content (deleted
      // placeholders), and scroll is guarded by stickToBottom anyway.
      setMessages(docs);
      if (fresh) setLiveConvo(fresh);
      const last = docs[docs.length - 1];
      if (last && last.sender_id !== me) markRead(convo.conversation_id).catch(() => {});
    } catch {
      if (!silent) toast.error("Could not load messages");
    }
  };

  // The parent list poll (8s) also hands down fresher convo objects.
  useEffect(() => { setLiveConvo(convo); }, [convo]);

  // Read receipt: your latest message + who among the OTHER members has
  // read up to it. Direct chat → "Seen"; group → "Seen by A, B (+n)".
  const lastMine = [...messages].reverse().find((m) => m.sender_id === me && !m.deleted && !m.call);
  const isLastOverall = lastMine && messages[messages.length - 1]?.message_id === lastMine.message_id;
  const seenBy = isLastOverall
    ? (liveConvo.members || []).filter((m) => m.user_id !== me && m.last_read_at && m.last_read_at >= lastMine.created_at)
    : [];
  const seenLabel = seenBy.length === 0
    ? null
    : liveConvo.is_group
      ? `Seen by ${seenBy.slice(0, 3).map((m) => (m.name || "").split(" ")[0]).filter(Boolean).join(", ")}${seenBy.length > 3 ? ` +${seenBy.length - 3}` : ""}`
      : "Seen";

  useEffect(() => {
    setMessages([]);
    setMention(null);
    setReplyTo(null);
    load(false);
    markRead(convo.conversation_id).catch(() => {});
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convo.conversation_id]);

  useEffect(() => {
    if (stickToBottom.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // iPhone: opening the keyboard shrinks the list (the dock follows the
  // visual viewport) but the browser doesn't re-pin it, so the last message
  // slides out of view. Re-pin on any size change while we're at the bottom,
  // and undo Safari's own page scroll so the fixed dock stays put.
  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const pin = () => {
      if (!stickToBottom.current) return;
      el.scrollTop = el.scrollHeight;
      if (window.scrollY) window.scrollTo(0, 0);
    };
    const ro = new ResizeObserver(() => requestAnimationFrame(pin));
    ro.observe(el);
    const v = window.visualViewport;
    v?.addEventListener("resize", pin);
    return () => { ro.disconnect(); v?.removeEventListener("resize", pin); };
  }, []);

  const onComposerFocus = () => {
    stickToBottom.current = true;
    // keyboard animation takes ~300ms on iOS; pin once it has settled too
    [50, 350, 700].forEach((ms) => setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      if (window.scrollY) window.scrollTo(0, 0);
    }, ms));
  };

  const onScroll = (e) => {
    stickToBottom.current = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 80;
  };

  const doSend = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft("");
    setMention(null);
    const quoting = replyTo;
    setReplyTo(null);
    // Instant local echo — the poll reconciles with the server copy.
    const tempId = `tmp-${Date.now()}`;
    setMessages((ms) => [...ms, {
      message_id: tempId, sender_id: me, body, created_at: new Date().toISOString(),
      sender_name: null, pending: true,
      ...(quoting ? { reply_to: { sender_name: quoting.sender_name, preview: (quoting.body || "").slice(0, 80) } } : {}),
    }]);
    stickToBottom.current = true;
    try {
      await sendMessage(convo.conversation_id, body, null, quoting?.message_id);
      await load();
    } catch {
      setMessages((ms) => ms.filter((m) => m.message_id !== tempId));
      toast.error("Message failed to send");
      setDraft(body);
      setReplyTo(quoting);
    } finally {
      setSending(false);
    }
  };

  const doAttach = async (file) => {
    if (!file) return;
    if (!/^(image|video)\//.test(file.type)) {
      toast.error("Only photos and videos");
      return;
    }
    setUploadPct(0);
    try {
      const fileId = await uploadChatMedia(convo.conversation_id, file, setUploadPct);
      await sendMessage(convo.conversation_id, "", fileId);
      stickToBottom.current = true;
      await load();
    } catch {
      toast.error("Upload failed — try again");
    } finally {
      setUploadPct(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doDeleteMsg = async (mid) => {
    try {
      await deleteMessage(convo.conversation_id, mid);
      setConfirmMsg(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the message");
    }
  };

  const doRemoveMember = async (uid) => {
    try {
      await removeMember(convo.conversation_id, uid);
      toast.success("Removed from the group");
      convo.members = (convo.members || []).filter((m) => m.user_id !== uid);
      convo.member_count = (convo.member_count || 1) - 1;
      forceTick((n) => n + 1); // instant feedback; the 8s list poll reconciles
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove them");
    }
  };

  const doRename = async () => {
    const name = nameDraft.trim();
    if (!name || renameBusy) return;
    setRenameBusy(true);
    try {
      await renameConversation(convo.conversation_id, name);
      convo.name = name; // instant feedback; the polls reconcile everywhere else
      setLiveConvo((c) => ({ ...c, name }));
      setRenaming(false);
      toast.success("Group renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename the group");
    } finally {
      setRenameBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-thread">
      {/* thread header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-2">
        <button onClick={onBack} data-testid="chat-back-btn" aria-label="Back to chats" className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Avatar name={convo.name} picture={convo.picture} online={convo.online} lastActive={convo.peer_last_active} size={34} testId="chat-thread-avatar" />
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex items-center gap-1" data-testid="chat-rename-form">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doRename(); if (e.key === "Escape") setRenaming(false); }}
                autoFocus
                maxLength={80}
                placeholder="Group name"
                data-testid="chat-rename-input"
                className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-0.5 text-sm text-gray-900 focus:border-[#1a3fd1] focus:outline-none"
              />
              <button onClick={doRename} disabled={!nameDraft.trim() || renameBusy} data-testid="chat-rename-save"
                aria-label="Save group name" className="rounded-full p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-30">
                {renameBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button onClick={() => setRenaming(false)} data-testid="chat-rename-cancel"
                aria-label="Cancel rename" className="rounded-full p-1 text-gray-400 hover:bg-gray-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-1">
              <p className="truncate text-sm font-semibold text-gray-900">{convo.name}</p>
              {convo.is_group && isAdmin && (
                <button
                  onClick={() => { setNameDraft(convo.name || ""); setRenaming(true); }}
                  data-testid="chat-rename-btn"
                  aria-label="Rename group"
                  title="Rename group"
                  className="shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <PencilLine className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <p className="text-[11px] text-gray-400">
            {(() => {
              const typers = (liveConvo.typing || []).filter((uid) => uid !== me);
              if (typers.length > 0) {
                const names = typers.map((uid) => (person(uid).name || "Someone").split(" ")[0]);
                return (
                  <span className="font-medium text-[#1a3fd1]" data-testid="chat-typing">
                    {convo.is_group ? `${names.join(", ")} ${typers.length > 1 ? "are" : "is"} typing…` : "typing…"}
                  </span>
                );
              }
              const away = minutesAway(convo.peer_last_active);
              if (away !== null) {
                return away < 5
                  ? <span className="text-emerald-600" data-testid="chat-presence-label">Active now</span>
                  : <span className="text-gray-400" data-testid="chat-presence-label">{away}m away</span>;
              }
              return convo.online ? <span className="text-emerald-600">Active now</span> : "Offline";
            })()}
            {convo.is_group ? ` · ${convo.member_count} people` : ""}
          </p>
        </div>
        {convo.is_group && (
          <button onClick={() => setShowMembers((s) => !s)}
            data-testid="chat-members-btn" aria-label="Group members" title="Group members"
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100">
            <Users className="h-[18px] w-[18px]" />
          </button>
        )}
        <button onClick={() => requestCall(convo.conversation_id, "audio", convo.name)}
          data-testid="chat-audio-call-btn" aria-label="Start voice call" title="Voice call"
          className="rounded-full p-2 text-[#1a3fd1] hover:bg-blue-50">
          <Phone className="h-[18px] w-[18px]" />
        </button>
        <button onClick={() => requestCall(convo.conversation_id, "video", convo.name)}
          data-testid="chat-video-call-btn" aria-label="Start video call" title="Video call"
          className="rounded-full p-2 text-[#1a3fd1] hover:bg-blue-50">
          <Video className="h-[18px] w-[18px]" />
        </button>
        <button onClick={onMinimize} data-testid="chat-thread-minimize-btn" aria-label="Minimize chat" title="Minimize"
          className="rounded-full p-2 text-gray-500 hover:bg-gray-100">
          <Minus className="h-[18px] w-[18px]" />
        </button>
        <button onClick={onClose} data-testid="chat-thread-close-btn" aria-label="Close chat" title="Close"
          className="rounded-full p-2 text-gray-500 hover:bg-gray-100">
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* group members manager — admins can remove people */}
      {showMembers && convo.is_group && (
        <div className="max-h-48 shrink-0 overflow-y-auto border-b border-gray-200 bg-gray-50 px-3 py-2" data-testid="chat-members-panel">
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-gray-400">{convo.member_count} members</p>
          {(convo.members || []).map((m) => (
            <div key={m.user_id} className="flex items-center gap-2 py-1" data-testid={`chat-member-${m.user_id}`}>
              <Avatar name={m.name} picture={m.picture} online={m.online} lastActive={m.last_active} size={26} />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{m.name}</span>
              {isAdmin && m.user_id !== me && (
                <button onClick={() => doRemoveMember(m.user_id)} data-testid={`chat-member-remove-${m.user_id}`}
                  className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-semibold text-red-500 shadow-sm hover:bg-red-50">
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div ref={listRef} onScroll={onScroll} className="min-h-0 flex-1 space-y-1 overflow-y-auto bg-[#f3f4f6] px-3 py-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="py-10 text-center text-xs text-gray-400">No messages yet — say hi.</p>
        )}
        {messages.map((m, i) => {
          if (m.call) {
            return (
              <CallLogRow key={m.message_id} msg={m} isGroup={convo.is_group}
                onCallBack={() => requestCall(convo.conversation_id, m.call.kind, convo.name)} />
            );
          }
          const mine = m.sender_id === me;
          const prev = messages[i - 1];
          const next = messages[i + 1];
          const showSender = !mine && convo.is_group && !sameMinuteGroup(prev, m);
          const grouped = sameMinuteGroup(prev, m);
          const showTime = !sameMinuteGroup(m, next);
          const sender = person(m.sender_id);
          return (
            <div key={m.message_id} className={`group flex ${mine ? "justify-end" : "justify-start"} ${grouped ? "mt-0.5" : "mt-2"}`} data-testid={`chat-msg-${m.message_id}`}>
              {!mine && (
                <div className="mr-1.5 w-6 shrink-0">
                  {!grouped && <Avatar name={sender.name || m.sender_name} picture={sender.picture} online={false} size={24} showStatus={false} />}
                </div>
              )}
              {/* hover reply shortcut (desktop); phones long-press the bubble */}
              {mine && !m.deleted && (
                <button onClick={() => { setReplyTo(m); composerRef.current?.focus(); }} data-testid={`chat-reply-btn-${m.message_id}`}
                  aria-label="Reply" title="Reply"
                  className="mx-1 self-center rounded-full p-1.5 text-gray-300 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-500 group-hover:opacity-100">
                  <Reply className="h-3.5 w-3.5" />
                </button>
              )}
              <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
                {showSender && <p className="mb-0.5 ml-2 text-[10px] font-medium text-gray-500">{m.sender_name}</p>}
                {m.reply_to && (
                  <div
                    className={`mb-1 rounded-xl border-l-2 px-2.5 py-1 text-[11px] ${mine ? "border-white/50 bg-[#1633b8] text-white/80" : "border-[#1a3fd1]/50 bg-gray-100 text-gray-500"}`}
                    data-testid={`chat-quote-${m.message_id}`}
                  >
                    <p className="font-semibold">{m.reply_to.sender_name}</p>
                    {m.reply_to.deleted
                      ? <p className="italic">Message deleted</p>
                      : <p className="truncate">{m.reply_to.preview}</p>}
                  </div>
                )}
                {m.deleted ? (
                  <div className="rounded-2xl border border-gray-200 px-3 py-1.5 text-xs italic text-gray-400" data-testid={`chat-msg-deleted-${m.message_id}`}>
                    Message deleted
                  </div>
                ) : (
                  <div
                    onClick={mine ? () => {
                      if (lpFired.current) { lpFired.current = false; return; }
                      setConfirmMsg(confirmMsg === m.message_id ? null : m.message_id);
                    } : undefined}
                    onTouchStart={() => startLongPress(m)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    title={mine ? "Tap your message to delete it" : undefined}
                    className={`${m.attachment && !m.body ? "" : "px-3 py-1.5"} rounded-2xl text-sm leading-snug ${
                      mine ? "cursor-pointer bg-[#1a3fd1] text-white" : "bg-white text-gray-900 shadow-sm"
                    }`}
                  >
                    {m.attachment && <Attachment fileId={m.attachment.file_id} kind={m.attachment.kind} name={m.attachment.name} />}
                    {m.body && <p className="whitespace-pre-wrap break-words">{renderWithMentions(m.body, mine)}</p>}
                  </div>
                )}
                {mine && confirmMsg === m.message_id && !m.deleted && (
                  <div className="mt-1 flex justify-end gap-1" data-testid={`chat-msgdel-${m.message_id}`}>
                    <button onClick={() => doDeleteMsg(m.message_id)} data-testid={`chat-msgdel-yes-${m.message_id}`}
                      className="rounded-full bg-red-500 px-2.5 py-0.5 text-[10px] font-semibold text-white hover:bg-red-600">
                      Delete
                    </button>
                    <button onClick={() => setConfirmMsg(null)}
                      className="rounded-full bg-gray-200 px-2.5 py-0.5 text-[10px] font-semibold text-gray-600">
                      Cancel
                    </button>
                  </div>
                )}
                {showTime && (
                  <p className={`mt-0.5 text-[9px] text-gray-400 ${mine ? "mr-1 text-right" : "ml-1"}`}
                    data-testid={`chat-time-${m.message_id}`}>
                    {fmtMsgTime(m.created_at)}
                  </p>
                )}
              </div>
              {!mine && !m.deleted && (
                <button onClick={() => { setReplyTo(m); composerRef.current?.focus(); }} data-testid={`chat-reply-btn-${m.message_id}`}
                  aria-label="Reply" title="Reply"
                  className="mx-1 self-center rounded-full p-1.5 text-gray-300 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-500 group-hover:opacity-100">
                  <Reply className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {seenLabel && (
          <div className="mt-0.5 flex justify-end pr-1" data-testid="chat-seen">
            <span className="text-[10px] font-medium text-gray-400">{seenLabel}</span>
          </div>
        )}
      </div>

      {/* reply preview — quoting a message */}
      {replyTo && (
        <div className="flex shrink-0 items-center gap-2 border-t border-gray-200 bg-gray-50 px-3 py-1.5" data-testid="chat-reply-preview">
          <Reply className="h-3.5 w-3.5 shrink-0 text-[#1a3fd1]" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold text-[#1a3fd1]">
              Replying to {replyTo.sender_id === me ? "yourself" : replyTo.sender_name}
            </p>
            <p className="truncate text-[11px] text-gray-500">
              {replyTo.deleted ? "Message deleted" : replyTo.body || (replyTo.attachment ? `Sent a ${replyTo.attachment.kind}` : "")}
            </p>
          </div>
          <button onClick={() => setReplyTo(null)} data-testid="chat-reply-cancel" aria-label="Cancel reply"
            className="shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* mention picker — appears while typing @ in a group */}
      {mention && mentionOptions.length > 0 && (
        <div className="shrink-0 border-t border-gray-200 bg-white py-1" data-testid="chat-mention-list">
          {mentionOptions.map((mm, i) => (
            <button
              key={mm.user_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); insertMention(mm); }}
              onMouseEnter={() => setMentionIdx(i)}
              data-testid={`chat-mention-${mm.user_id}`}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${i === mentionIdx ? "bg-blue-50" : ""}`}
            >
              <Avatar name={mm.name} picture={mm.picture} size={26} showStatus={false} />
              <span className="truncate text-sm text-gray-800">{mm.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* composer — safe-area padding keeps it above the iPhone home bar */}
      <div className="flex shrink-0 items-center gap-2 border-t border-gray-200 bg-white px-3 py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
        <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => doAttach(e.target.files?.[0])} />
        <button
          onClick={() => fileRef.current?.click()}
          data-testid="chat-attach-btn"
          aria-label="Send a photo or video"
          className="rounded-full p-2 text-[#1a3fd1] hover:bg-blue-50"
        >
          <ImagePlus className="h-5 w-5" />
        </button>
        <input
          ref={composerRef}
          value={draft}
          onChange={onDraftChange}
          onKeyDown={onComposerKeyDown}
          onFocus={onComposerFocus}
          onBlur={resetPageZoom}
          placeholder={uploadPct != null ? `Uploading… ${uploadPct}%` : "Write a message…"}
          data-testid="chat-input"
          className="min-w-0 flex-1 rounded-full border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm text-gray-900 focus:border-[#1a3fd1] focus:outline-none"
        />
        <button
          onClick={doSend}
          disabled={!draft.trim() || sending}
          data-testid="chat-send-btn"
          aria-label="Send"
          className="rounded-full p-2 text-[#1a3fd1] hover:bg-blue-50 disabled:opacity-30"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
      {uploadPct != null && (
        <div className="h-1 w-full bg-gray-100">
          <div className="h-1 bg-[#1a3fd1] transition-all" style={{ width: `${uploadPct}%` }} data-testid="chat-upload-progress" />
        </div>
      )}
    </div>
  );
}
